import { clx, Table } from '@medusajs/ui';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';

import { DealRecordType, type DealRecord } from '@marxbiotech/shared-types/bff';

import { convertToLocale } from '@lib/util/money';

const TableHeader = () => (
  <Table.Header className="border-none">
    <Table.Row className="bg-white-20 text-black-10 pb-6 text-base font-normal">
      <Table.Cell>日期</Table.Cell>
      <Table.Cell>說明</Table.Cell>
      <Table.Cell>金額</Table.Cell>
    </Table.Row>
  </Table.Header>
);

const DisplayAmount = ({ amount }: { amount: number }) => {
  return (
    <span
      className={clx({
        'text-red-60': amount < 0,
      })}
    >
      {amount > 0 && '+'}
      {convertToLocale({
        amount,
        currency_code: 'usd',
      })}
    </span>
  );
};

// Design Decision: 不為 getDescription 加單元測試，此函式為純 UI 顯示對應，
// 各 case 邏輯簡單且與 v1.14 store-client 一致，透過 E2E 驗證即可
const getDescription = (
  record: DealRecord,
  countryCode: string,
): React.ReactNode => {
  const details = record.details;

  switch (record.type) {
    case DealRecordType.CHARGE:
      if (details?.orderId && details.orderNumber && details.orderNumber !== 'to-be-updated') {
        return (
          <Link
            to={`/${countryCode}/account/orders/details/${details.orderId}`}
            className="underline underline-offset-2"
          >
            訂單單號 {details.orderNumber}
          </Link>
        );
      }
      return <span>訂單扣款</span>;
    case DealRecordType.REFUND:
      return <span>退貨回饋金</span>;
    case DealRecordType.ADJUST:
      return (
        <span>
          餘額調整{details?.description ? `：${details.description}` : ''}
        </span>
      );
    case DealRecordType.REWARD:
      return <span>回饋金</span>;
    case DealRecordType.DEPOSIT:
      return <span>儲值金</span>;
    case DealRecordType.LOAN_PAYOUT:
      if (details?.orderId && details.orderNumber && details.orderNumber !== 'to-be-updated') {
        return (
          <Link
            to={`/${countryCode}/account/orders/details/${details.orderId}`}
            className="underline underline-offset-2"
          >
            賒帳 使用於訂單單號 {details.orderNumber}
          </Link>
        );
      }
      return <span>訂單賒帳</span>;
    case DealRecordType.LOAN_REPAYMENT:
      return <span>還款</span>;
    case DealRecordType.COD_REQUEST:
      return <span>退貨退款金</span>;
    case DealRecordType.COD_REFUND:
      return <span>退款貨到付款</span>;
    case DealRecordType.COD_ADJUST:
      return (
        <span>
          退貨退款餘額調整
          {details?.description ? `：${details.description}` : ''}
        </span>
      );
    default:
      return <span>{record.type}</span>;
  }
};

// 兩個頁面共用此 table，但金額欄的語意不同：
// - 'amount'（預設，儲值金紀錄頁）：顯示 record.amount，CHARGE/LOAN_* 類型翻正負
// - 'codRefundDifference'（退貨退款餘額頁）：直接顯示對退款餘額的有號影響值
type AmountField = 'amount' | 'codRefundDifference';

const getDisplayAmount = (
  record: DealRecord,
  amountField: AmountField,
): number => {
  if (amountField === 'codRefundDifference') {
    return record.codRefundDifference ?? 0;
  }

  const deductTypes = [
    DealRecordType.CHARGE,
    DealRecordType.LOAN_PAYOUT,
    DealRecordType.LOAN_REPAYMENT,
  ];

  if (deductTypes.includes(record.type as DealRecordType)) {
    return -record.amount;
  }

  return record.amount;
};

interface DealRecordTableProps {
  dealRecords: DealRecord[];
  amountField?: AmountField;
}

export const DealRecordTable = ({
  dealRecords,
  amountField = 'amount',
}: DealRecordTableProps) => {
  const { countryCode = 'tw' } = useParams<{ countryCode: string }>();

  return (
    <div className="w-full" data-testid="deal-records-wrapper">
      {dealRecords && dealRecords.length !== 0 ? (
        <div className="flex flex-col">
          <div className="border-b border-gray-200 pb-6 last:border-none last:pb-0">
            <Table>
              <TableHeader />
              <Table.Body>
                {dealRecords.map((record) => (
                  <Table.Row
                    className={clx('text-black-30 h-14 px-2 py-4 text-base', {
                      'bg-white-20':
                        record.type ===
                        DealRecordType.CHARGE,
                      'bg-white-10':
                        record.type !==
                        DealRecordType.CHARGE,
                    })}
                    key={record.id}
                  >
                    <Table.Cell className="w-1/3">
                      {dayjs(record.dealtAt).format('YYYY/MM/DD HH:mm')}
                    </Table.Cell>
                    <Table.Cell className="w-1/3">
                      {getDescription(record, countryCode)}
                    </Table.Cell>
                    <Table.Cell className="w-1/3">
                      <DisplayAmount
                        amount={getDisplayAmount(record, amountField)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
      ) : (
        <div
          className="mt-[69px] flex w-full flex-col items-center gap-y-4 text-center text-base font-normal"
          data-testid="no-payment-container"
        >
          <p className="text-black-10">
            目前尚未有紀錄，若您有任何疑問或需要協助請洽您的業務專員或是聯繫客服。
            <br />
          </p>
          <p className="text-grey-60">
            客服郵箱：marxpath@marxbiotech.com
            <br />
            客服電話：02-82455250（服務時間：週一至週五，9:00至18:00）
            <br />
            感謝您的聯繫，期待盡快為您服務！
          </p>
        </div>
      )}
    </div>
  );
};

export default DealRecordTable;
